"use client";

import { useTranslations } from "next-intl";
import { Select } from "@/app/components/ui/Select";
import { getGetCoachProfileQueryKey, getListCoachProfilesQueryKey } from "@/app/generated/api/coach/coach";
import { getGetClientPortalBookingCatalogQueryKey, getListClientPortalsQueryKey } from "@/app/generated/api/client-portal/client-portal";
import { bookingQueryKeys } from "@/app/modules/booking/hooks/useBookingQueries";
import { preparePersonalTrainerInput, resolvePersonalTrainerSelections } from "./personalTrainerSelections";
import { createPersonalTrainerInput, validPersonalTrainerSetup } from "./personalTrainerServices";
import { SetupServicesFields } from "./SetupServicesFields";
import type { IndustrySetupDriver, IndustrySetupFieldsProps } from "./types";

function PersonalTrainerSetupFields({ context, input, onChange }: IndustrySetupFieldsProps) {
  const t = useTranslations("apps.industrySetup");
  const { groups, needsSelection } = resolvePersonalTrainerSelections(context, input);
  const hasExistingSetup = groups.some((group) => group.options.length > 0);
  const services = input.personalTrainerSetup?.services ?? [{
    key: "service-1", name: t("personalTrainer.defaultService"), durationMinutes: input.defaultDurationMinutes ?? 60, price: null,
  }];

  return (
    <div className="space-y-6">
      {!input.installOnly ? (
        <>
          <SetupServicesFields
            namespace="apps.industrySetup.personalTrainer"
            services={services}
            onChange={(services) => onChange({ personalTrainerSetup: { services } })}
            createService={() => ({ key: `service-${crypto.randomUUID()}`, name: "", durationMinutes: 60, price: null })}
          />
          {!validPersonalTrainerSetup({ ...input, personalTrainerSetup: { services } }) ? (
            <p className="text-sm text-text-secondary" role="status">{t("personalTrainer.servicesValidation")}</p>
          ) : null}
        </>
      ) : null}

      {hasExistingSetup ? (
        <details className="rounded-xl border border-border bg-bg-elevated p-4" open={needsSelection || undefined}>
          <summary className="cursor-pointer text-sm font-semibold text-text">
            {t("personalTrainer.existingSetup")}
          </summary>
          <p className="mt-3 text-sm text-text-secondary">{t("personalTrainer.keepExisting")}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {groups.filter((group) => group.options.length > 0).map((group) => (
              group.options.length > 1 ? (
                <Select
                  key={group.field}
                  label={t(`reuse.fields.${group.field}`)}
                  required
                  value={group.value ?? ""}
                  onChange={(event) => {
                    const value = event.target.value || null;
                    onChange({
                      [group.field]: value,
                      ...group.field === "companyUuid" ? { branchUuid: null } : {},
                      ...group.field === "coachProfileUuid" ? {
                        bookingPortalUuid: context.coachProfiles.find((profile) => profile.uuid === value)?.parentUuid ?? null,
                        bookingResourceUuid: null,
                        bookingServiceUuid: null,
                      } : {},
                      ...group.field === "bookingPortalUuid" ? {
                        bookingResourceUuid: null,
                        bookingServiceUuid: null,
                      } : {},
                      ...group.field === "bookingResourceUuid" ? { bookingServiceUuid: null } : {},
                    });
                  }}
                >
                  <option value="">{t("reuse.choose")}</option>
                  {group.options.map((option) => (
                    <option key={option.uuid} value={option.uuid}>{option.name}</option>
                  ))}
                </Select>
              ) : (
                <div key={group.field} className="text-sm">
                  <p className="text-text-muted">{t(`reuse.fields.${group.field}`)}</p>
                  <p className="mt-1 font-medium text-text">{group.options[0].name}</p>
                </div>
              )
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

export const personalTrainerSetup: IndustrySetupDriver = {
  presetCode: "personal-trainer",
  titleKey: "apps.presets.items.personalTrainer.title",
  defaultNameKey: "personalTrainer.defaultName",
  descriptionKey: "personalTrainer.description",
  workspaceTitleKey: "personalTrainer.workspaceTitle",
  showGuidance: false,
  completionLabelKey: "personalTrainer.complete",
  setupSteps: ["personalTrainer.services"],
  defaultInput: { defaultDurationMinutes: 60, selectedRecommendedAppCodes: null, installOnly: false },
  createInput: createPersonalTrainerInput,
  isValid: validPersonalTrainerSetup,
  prepareInput: preparePersonalTrainerInput,
  requiredRecommendedApps: ["coach"],
  Fields: PersonalTrainerSetupFields,
  resolveSelections: resolvePersonalTrainerSelections,
  destination: (workspaceUuid, result) => result.completed && result.coachProfileUuid
    ? `/c/${workspaceUuid}/coach/${result.coachProfileUuid}?addClient=1`
    : `/c/${workspaceUuid}/apps/presets`,
  invalidationKeys: (workspaceUuid, result) => [
    getListCoachProfilesQueryKey(workspaceUuid),
    getListClientPortalsQueryKey(workspaceUuid),
    bookingQueryKeys.all(workspaceUuid),
    ...result.coachProfileUuid ? [getGetCoachProfileQueryKey(workspaceUuid, result.coachProfileUuid)] : [],
    ...result.clientPortalUuid ? [getGetClientPortalBookingCatalogQueryKey(workspaceUuid, result.clientPortalUuid)] : [],
  ],
};
